import {useLocale} from '@epic-mw/i18n';
import {useCountry} from '../geo';
import {NAV_OWNER} from '@/constants';
import {useMemo} from 'react';

type QuickLink =
	| 'storeRefundPolicy'
	| 'fnCrew'
	| 'itemShop'
	| 'achievements'
	| 'epicRewards'
	| 'accountBalance'
	| 'accountSubscriptions'
	| 'coupons'
	| 'account'
	| 'storeRedeem'
	| 'fnRedeem'
	| 'vbucksRedeem'
	| 'wishlist'
	| 'accountBalanceTerms'
	| 'giftCardTerms'
	| 'store'
	| 'fab'
	| 'faqAccountBalance'
	| 'help'
	| 'helpRedeem';

export const useQuickLinks = <T extends QuickLink>(
	keys: T | T[],
	searchParams?: Record<string, string>
): Record<T, string> => {
	const locale = useLocale();
	const country = useCountry();
	const toReturn = {} as Record<T, string>;

	const applySearchParams = (link: string) => {
		try {
			const url = new URL(link);

			url.searchParams.set('source', NAV_OWNER);

			// TODO: Can set lang here if different TLD.

			if (typeof searchParams === 'object') {
				for (const key in searchParams) {
					url.searchParams.set(key, searchParams[key]);
				}
			}

			return url.toString();
		} catch {
			console.error('Failed to apply source');
			return link;
		}
	};

	const getUrl = (key: QuickLink) => {
		switch (key) {
			case 'helpRedeem':
				return `${process.env.NEXT_PUBLIC_EPIC_BASE_URL}/help/c-Category_TechnicalSupport/c-TechnicalSupport_GeneralSupport/how-do-i-redeem-a-code-to-my-epic-games-account-a000084732`;
			case 'faqAccountBalance':
				return `${process.env.NEXT_PUBLIC_EPIC_BASE_URL}/account-balance-faq`;
			case 'help':
				return `${process.env.NEXT_PUBLIC_EPIC_BASE_URL}/help`;
			case 'storeRefundPolicy':
				return `${process.env.NEXT_PUBLIC_EPIC_LEGAL_URL}/store/refund-policy`;
			case 'fnCrew':
				return `${process.env.NEXT_PUBLIC_FORTNITE_URL}/fortnite-crew-subscription`;
			case 'itemShop':
				return `${process.env.NEXT_PUBLIC_FORTNITE_URL}/item-shop`;
			case 'achievements':
				return `${process.env.NEXT_PUBLIC_STORE_URL}/u`;
			case 'epicRewards':
				return `${process.env.NEXT_PUBLIC_EPIC_BASE_URL}/account/rewards`;
			case 'accountBalance':
				return `${process.env.NEXT_PUBLIC_EPIC_BASE_URL}/account/payments`;
			case 'accountSubscriptions':
				return `${process.env.NEXT_PUBLIC_EPIC_BASE_URL}/account/subscriptions`;
			case 'coupons':
				return `${process.env.NEXT_PUBLIC_STORE_URL}/inventory/coupons`;
			case 'account':
				return `${process.env.NEXT_PUBLIC_EPIC_BASE_URL}/account/personal`;
			case 'storeRedeem':
				return `${process.env.NEXT_PUBLIC_STORE_URL}/redeem`;
			case 'fnRedeem':
				return `${process.env.NEXT_PUBLIC_FORTNITE_URL}/redeem`;
			case 'vbucksRedeem':
				return `${process.env.NEXT_PUBLIC_FORTNITE_URL}/vbuckscard`;
			case 'wishlist':
				return `${process.env.NEXT_PUBLIC_STORE_URL}/wishlist`;
			case 'accountBalanceTerms':
				const link = `${process.env.NEXT_PUBLIC_EPIC_LEGAL_URL}/epicgames/account-balance-terms`;
				if (country === 'JP') {
					return `${link}?eulaKey=wallet-terms_JP`;
				}
				return link;
			case 'giftCardTerms':
				return `${process.env.NEXT_PUBLIC_EPIC_LEGAL_URL}/epicgames/gift-card-terms`;
			case 'store':
				return process.env.NEXT_PUBLIC_STORE_URL;
			case 'fab':
				return process.env.NEXT_PUBLIC_FAB_URL;
			default:
				return '#';
		}
	};

	return useMemo(() => {
		if (typeof keys === 'string') {
			toReturn[keys] = applySearchParams(getUrl(keys));
		} else {
			keys.reduce((prev, key) => {
				prev[key] = applySearchParams(getUrl(key));
				return prev;
			}, toReturn);
		}

		return toReturn;
	}, [locale, searchParams ? JSON.stringify(searchParams) : undefined]);
};
